/**
 * The Check create/edit form (Feature: granular-crud-api, task 13.1; Requirements 11.1, 11.2, 11.3,
 * 11.5, 11.6; Design: §11).
 *
 * One component serves both writes, because they differ in exactly two places: `projectId` is an
 * author field on create and an immutable field on patch (the API rejects it in a patch body), and
 * the initial values come from the loaded entity. Everything else — the field set, the type-specific
 * spec sub-forms, the error placement — is shared, so create and edit cannot drift.
 *
 * A third `draft` mode was added later (ai-check-generation task 23.5) for reviewing a generated
 * Draft_Check, which is a Check *payload* and so needs this exact field set; see
 * {@link CheckFormProps} for why it is its own arm rather than a reuse of `edit`.
 *
 * Three deliberate choices:
 *
 *  - **The type selector shows all three Check types, with `browser` disabled and labelled "not yet
 *    executable"** (Req 11.2). The platform models Browser_Checks but does not execute them yet;
 *    hiding the type would misrepresent the model, and enabling it would let a user create a Check
 *    that never runs. Submission is restricted to `uptime`/`api` (Req 11.3) — enforced by
 *    {@link isSubmittableType} at the one place submission happens, so an existing browser Check
 *    (only the manifest path can create one) opens read-only rather than being silently editable.
 *
 *  - **Numeric and list inputs are held as the user's raw text and parsed at submit**, with an
 *    unparseable value sent through verbatim. The server is the single validator (Req 10.1/10.3), so
 *    a bad number must come back as a per-field error on that field rather than being swallowed or
 *    silently coerced here. Keeping the raw text is also what lets a failed submit preserve exactly
 *    what the user typed (Req 11.6).
 *
 *  - **The form never touches the caller's data.** It owns only its own draft state and reports the
 *    body to submit; the page owns the request, the error, and any navigation (Req 11.6).
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import type { CheckType, FieldError, HttpMethod, UptimeMode } from '@soroush/core';

import type { CheckDto, CheckPatch, CheckSpecPayload, CreateCheckPayload } from '../api/types';

import { locationLabel } from '../lib/locations';

import { FormErrorPanel, FormField, unmappedFieldErrors } from './FieldErrorText';
import { InfoTooltip } from './InfoTooltip';

/** The Check types this form can submit (Req 11.3). */
const SUBMITTABLE_TYPES: readonly CheckType[] = ['uptime', 'api'];

/** Whether a type may be written through the dashboard (Req 11.3). */
export function isSubmittableType(type: CheckType): boolean {
  return SUBMITTABLE_TYPES.includes(type);
}

const TYPE_OPTIONS: readonly { value: CheckType; label: string; disabled?: true }[] = [
  { value: 'uptime', label: 'Uptime monitor' },
  { value: 'api', label: 'API check' },
  // Modelled by the platform, not yet executable by the Check_Execution_Service (Req 11.2).
  { value: 'browser', label: 'Browser check (not yet executable)', disabled: true },
];

const UPTIME_MODES: readonly UptimeMode[] = ['url', 'tcp', 'heartbeat'];

const HTTP_METHODS: readonly HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

/* -------------------------------------------------------------------------- */
/* Draft state                                                                 */
/* -------------------------------------------------------------------------- */

interface StepDraft {
  method: HttpMethod;
  url: string;
  expectedStatus: string;
}

interface CheckDraft {
  projectId: string;
  logicalName: string;
  type: CheckType;
  intervalSeconds: string;
  /**
   * The Global_Locations the Check runs from, held as a list rather than the comma-separated text this
   * field used to be (Req 8.2).
   *
   * It was free text, and that was the shape of a real defect: a plausible region name the deployment
   * does not run from — or an empty field — produced a Check that was enabled, reported `up`, and never
   * executed, because the ScheduleIndex is partitioned by location and nothing scanned its partition.
   * Now it is chosen from the deployment's own list, so an unrunnable value is not expressible here.
   */
  locations: readonly string[];
  enabled: boolean;
  failureThreshold: string;
  maxRetries: string;
  latencySensitive: boolean;
  secretsRefs: string;
  uptimeMode: UptimeMode;
  target: string;
  statusMin: string;
  statusMax: string;
  port: string;
  connectTimeoutMs: string;
  heartbeatIntervalSeconds: string;
  responseTimeThresholdMs: string;
  steps: StepDraft[];
}

function text(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function specRecord(spec: unknown): Record<string, unknown> {
  return spec !== null && typeof spec === 'object' ? (spec as Record<string, unknown>) : {};
}

const EMPTY_STEP: StepDraft = { method: 'GET', url: '', expectedStatus: '200' };

/** The draft a create form starts from: platform defaults, everything else blank. */
function blankDraft(availableLocations: readonly string[]): CheckDraft {
  return {
    projectId: '',
    logicalName: '',
    type: 'uptime',
    intervalSeconds: '60',
    // Pre-selected rather than blank: "at least one location" is now a validation rule, and the surest
    // way to satisfy it is to make the empty state unreachable instead of reporting it after a submit.
    locations: availableLocations.length > 0 ? [availableLocations[0] as string] : [],
    enabled: true,
    failureThreshold: '1',
    maxRetries: '2',
    latencySensitive: false,
    secretsRefs: '',
    uptimeMode: 'url',
    target: '',
    statusMin: '200',
    statusMax: '299',
    port: '',
    connectTimeoutMs: '',
    heartbeatIntervalSeconds: '',
    responseTimeThresholdMs: '1000',
    steps: [{ ...EMPTY_STEP }],
  };
}

/** The draft an edit form starts from: the loaded entity, rendered back into input values. */
function draftFromCheck(check: CheckDto, availableLocations: readonly string[]): CheckDraft {
  const spec = specRecord(check.spec);
  const range = specRecord(spec.expectedStatusRange);
  const steps = Array.isArray(spec.steps) ? spec.steps : [];
  const blank = blankDraft(availableLocations);
  return {
    ...blank,
    projectId: text(check.projectId),
    logicalName: text(check.logicalName),
    type: check.type,
    intervalSeconds: text(check.intervalSeconds),
    // Verbatim, including a location this deployment no longer runs from: the picker surfaces it as
    // unavailable rather than silently swapping it, so an edit cannot quietly relocate a Check.
    locations: Array.isArray(check.locations) ? [...check.locations] : blank.locations,
    enabled: check.enabled !== false,
    failureThreshold: text(check.failureThreshold),
    maxRetries: text(check.maxRetries),
    latencySensitive: check.latencySensitive === true,
    secretsRefs: Array.isArray(check.secretsRefs) ? check.secretsRefs.map(text).join(', ') : '',
    uptimeMode: UPTIME_MODES.includes(spec.mode as UptimeMode) ? (spec.mode as UptimeMode) : 'url',
    target: text(spec.target),
    statusMin: range.min === undefined ? blank.statusMin : text(range.min),
    statusMax: range.max === undefined ? blank.statusMax : text(range.max),
    port: text(spec.port),
    connectTimeoutMs: text(spec.connectTimeoutMs),
    heartbeatIntervalSeconds: text(spec.heartbeatIntervalSeconds),
    responseTimeThresholdMs:
      spec.responseTimeThresholdMs === undefined
        ? blank.responseTimeThresholdMs
        : text(spec.responseTimeThresholdMs),
    steps:
      steps.length > 0
        ? steps.map((step) => {
            const record = specRecord(step);
            return {
              method: HTTP_METHODS.includes(record.method as HttpMethod)
                ? (record.method as HttpMethod)
                : 'GET',
              url: text(record.url),
              expectedStatus: text(record.expectedStatus),
            };
          })
        : [{ ...EMPTY_STEP }],
  };
}

/* -------------------------------------------------------------------------- */
/* Draft → wire body                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A numeric input's value for the wire: the parsed number, or the raw text when it does not parse so
 * the API reports the defect on that field (the server is the validator — Req 10.1, 10.3). The cast
 * is the honest expression of that: user input is `unknown` until the API has judged it.
 */
function numeric(raw: string): number {
  const trimmed = raw.trim();
  const parsed = Number(trimmed);
  const value = trimmed.length > 0 && Number.isFinite(parsed) ? parsed : trimmed;
  return value as number;
}

/** A comma-separated list input, blank entries dropped. */
function list(raw: string): string[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function specFromDraft(draft: CheckDraft): CheckSpecPayload {
  if (draft.type === 'api') {
    return {
      responseTimeThresholdMs: numeric(draft.responseTimeThresholdMs),
      steps: draft.steps.map((step) => ({
        method: step.method,
        url: step.url.trim(),
        expectedStatus: numeric(step.expectedStatus),
        assertions: [],
        extracts: [],
      })),
    };
  }
  const mode = draft.uptimeMode;
  return {
    mode,
    target: draft.target.trim(),
    ...(mode === 'url'
      ? { expectedStatusRange: { min: numeric(draft.statusMin), max: numeric(draft.statusMax) } }
      : {}),
    ...(mode === 'tcp'
      ? {
          ...(draft.port.trim().length > 0 ? { port: numeric(draft.port) } : {}),
          ...(draft.connectTimeoutMs.trim().length > 0
            ? { connectTimeoutMs: numeric(draft.connectTimeoutMs) }
            : {}),
        }
      : {}),
    ...(mode === 'heartbeat' && draft.heartbeatIntervalSeconds.trim().length > 0
      ? { heartbeatIntervalSeconds: numeric(draft.heartbeatIntervalSeconds) }
      : {}),
  };
}

/**
 * The mutable field set both bodies share — a patch is exactly this, a create adds `projectId`.
 * `secretsRefs` is always present (an empty list, not an omission) so that clearing the field in an
 * edit actually clears it: a merge patch leaves an omitted field at its stored value.
 */
function mutableFields(draft: CheckDraft): Omit<CreateCheckPayload, 'projectId'> {
  return {
    logicalName: draft.logicalName.trim(),
    type: draft.type,
    intervalSeconds: numeric(draft.intervalSeconds),
    locations: [...draft.locations],
    spec: specFromDraft(draft),
    enabled: draft.enabled,
    failureThreshold: numeric(draft.failureThreshold),
    maxRetries: numeric(draft.maxRetries),
    latencySensitive: draft.latencySensitive,
    secretsRefs: list(draft.secretsRefs),
  };
}

/** The `POST /v1/checks` body for a draft. */
export function createPayloadFromDraft(draft: CheckDraft): CreateCheckPayload {
  return { projectId: draft.projectId.trim(), ...mutableFields(draft) };
}

/**
 * The `PATCH /v1/checks/{id}` body for a draft: every mutable field the form renders, so the update
 * is a complete, idempotent statement of intent. `projectId` is excluded because the API rejects an
 * immutable field in a patch body.
 */
export function patchFromDraft(draft: CheckDraft): CheckPatch {
  return mutableFields(draft);
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/** Every error path this form renders a field for; the rest go to the form-level panel (Req 11.5). */
function mappedPaths(draft: CheckDraft): string[] {
  const paths = [
    'check.projectId',
    'check.logicalName',
    'check.type',
    'check.intervalSeconds',
    'check.locations',
    'check.enabled',
    'check.failureThreshold',
    'check.maxRetries',
    'check.latencySensitive',
    'check.secretsRefs',
  ];
  if (draft.type === 'api') {
    paths.push('check.spec.responseTimeThresholdMs', 'check.spec.steps');
    draft.steps.forEach((_step, index) => {
      paths.push(
        `check.spec.steps[${index}].method`,
        `check.spec.steps[${index}].url`,
        `check.spec.steps[${index}].expectedStatus`,
      );
    });
  } else {
    paths.push(
      'check.spec.mode',
      'check.spec.target',
      'check.spec.expectedStatusRange',
      'check.spec.port',
      'check.spec.connectTimeoutMs',
      'check.spec.heartbeatIntervalSeconds',
    );
  }
  return paths;
}

interface CheckFormCommon {
  /**
   * The Global_Locations this deployment runs Checks from (Req 8.2) — the picker's options.
   *
   * A **prop rather than a `useConfig()` read**, deliberately: this form takes no context dependency,
   * which is what lets its tests render it bare. The three pages that mount it are already wired to
   * context and pass `useConfig().runnerRegions`.
   *
   * Defaults to {@link DEFAULT_FORM_LOCATIONS} so a caller that has not been updated still renders a
   * usable picker. The server validates the choice regardless, so a stale default is refused rather
   * than silently accepted.
   */
  availableLocations?: readonly string[];
  /** Whether a submit is in flight; disables the controls without discarding the draft. */
  submitting?: boolean;
  /** Per-field errors from the last failed write (Req 11.5). */
  fieldErrors?: readonly FieldError[];
  /** The failed write's message, shown in the form-level panel (Req 11.6). */
  errorMessage?: string;
  /** Slot for the provenance notice on a manifest- or Terraform-managed entity (Req 11.8, 13.1). */
  notice?: ReactNode;
  /**
   * Hold the submit back for a reason the form itself cannot see (Feature: terraform-provider, task
   * 9.2; Req 13.3) — today, an unticked Terraform acknowledgement in {@link notice}.
   *
   * Distinct from {@link readOnly} and {@link submitting}: those disable every control, because a
   * viewer may not edit and an in-flight draft must not be re-entered. This one disables the **submit
   * alone** — the user is entitled to keep editing while they decide whether to acknowledge, and the
   * draft they typed before ticking the box must survive the tick.
   */
  submitDisabled?: boolean;
  /** A viewer sees the values but cannot submit (Req 11.7). */
  readOnly?: boolean;
  onCancel?: () => void;
}

/**
 * The three writes this form serves. `draft` was added for the Draft_Check review page (Feature:
 * ai-check-generation, task 23.5; Req 9.3, 9.5) and is deliberately its own arm rather than a reuse
 * of `edit`, because a draft sits between the other two: it is **pre-filled** like an edit, but
 * `projectId` is an **author field** like a create.
 *
 * `PATCH /v1/check-drafts/{id}` accepts `projectId` (only `draftId`, `accountId`, `state`, and
 * `generationRequestId` are immutable there), and a correction-exhausted draft can be persisted with
 * an empty configuration — so the reviewer repairing it must be able to supply the project. Under
 * `edit` the field is not rendered at all while `check.projectId` still appears in
 * {@link mappedPaths}, which would claim that error and then display it nowhere. So the third arm is
 * what makes the draft page's error placement complete, not a cosmetic preference.
 *
 * Its body is a full Check payload (`projectId` included), which the draft route's merge patch and
 * schema projection accept; `create` and `edit` are untouched by its presence.
 */
export type CheckFormProps = CheckFormCommon &
  (
    | { mode: 'create'; initial?: undefined; onSubmit: (payload: CreateCheckPayload) => void }
    | { mode: 'edit'; initial: CheckDto; onSubmit: (patch: CheckPatch) => void }
    | { mode: 'draft'; initial: CheckDto; onSubmit: (payload: CreateCheckPayload) => void }
  );

/**
 * The locations assumed when a caller passes none. Mirrors the platform's single runner region; the
 * real list arrives from `config.json`, and the server refuses anything it does not run from.
 */
export const DEFAULT_FORM_LOCATIONS: readonly string[] = ['eu-central-1'];

export function CheckForm(props: CheckFormProps): JSX.Element {
  const {
    submitting = false,
    fieldErrors,
    errorMessage,
    notice,
    submitDisabled = false,
    readOnly = false,
    availableLocations = DEFAULT_FORM_LOCATIONS,
  } = props;
  const [draft, setDraft] = useState<CheckDraft>(() =>
    props.mode === 'create'
      ? blankDraft(availableLocations)
      : draftFromCheck(props.initial, availableLocations),
  );

  /**
   * The options offered: the deployment's locations, plus any the loaded Check already holds.
   *
   * The union is what keeps an edit non-destructive. Offering only the available list would leave the
   * select unable to represent a stored value, and a `<select>` whose `value` matches no option renders
   * as the first one — silently rewriting the Check's location the moment the form is saved.
   */
  const locationOptions = [
    ...availableLocations,
    ...draft.locations.filter((location) => !availableLocations.includes(location)),
  ];
  /** The single value the picker binds to; the first stored location, or the first available one. */
  const selectedLocation = draft.locations[0] ?? locationOptions[0] ?? '';

  function update<K extends keyof CheckDraft>(key: K, value: CheckDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function updateStep(index: number, patch: Partial<StepDraft>): void {
    setDraft((current) => ({
      ...current,
      steps: current.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)),
    }));
  }

  const submittable = isSubmittableType(draft.type);
  const disabled = readOnly || submitting;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Req 11.3: the dashboard writes uptime and api Checks only. Enforced here, not just in the
    // selector, so an existing browser Check opened for edit cannot be submitted either.
    //
    // `submitDisabled` is checked here too, and not only on the button (Req 13.3). A disabled button
    // is a hint; a form can still be submitted by pressing Enter in a text input, and the requirement
    // is that no request is *issued* before the acknowledgement, not that the button looks unavailable.
    if (!submittable || disabled || submitDisabled) return;
    if (props.mode === 'edit') props.onSubmit(patchFromDraft(draft));
    // `create` and `draft` both submit a whole payload, `projectId` included.
    else props.onSubmit(createPayloadFromDraft(draft));
  }

  return (
    <form className="entity-form" onSubmit={handleSubmit} noValidate>
      {notice}
      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, mappedPaths(draft))}
      />

      {/* An author field on create and on a draft review; immutable on a Check patch. */}
      {props.mode !== 'edit' ? (
        <FormField
          label="Project"
          path="check.projectId"
          errors={fieldErrors}
          render={(field) => (
            <input
              {...field}
              type="text"
              value={draft.projectId}
              disabled={disabled}
              onChange={(event) => update('projectId', event.target.value)}
            />
          )}
        />
      ) : null}

      <FormField
        label="Name"
        path="check.logicalName"
        errors={fieldErrors}
        hint="Unique within the project."
        render={(field) => (
          <input
            {...field}
            type="text"
            value={draft.logicalName}
            disabled={disabled}
            onChange={(event) => update('logicalName', event.target.value)}
          />
        )}
      />

      <FormField
        label="Type"
        path="check.type"
        errors={fieldErrors}
        render={(field) => (
          <select
            {...field}
            value={draft.type}
            disabled={disabled}
            onChange={(event) => update('type', event.target.value as CheckType)}
          >
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      />

      {!submittable ? (
        <p role="note" className="muted" data-testid="type-not-executable">
          {draft.type} checks are not yet executable, so they cannot be created or saved from the
          dashboard.
        </p>
      ) : null}

      <FormField
        label="Interval (seconds)"
        path="check.intervalSeconds"
        errors={fieldErrors}
        render={(field) => (
          <input
            {...field}
            type="number"
            value={draft.intervalSeconds}
            disabled={disabled}
            onChange={(event) => update('intervalSeconds', event.target.value)}
          />
        )}
      />

      <FormField
        label="Location"
        path="check.locations"
        errors={fieldErrors}
        hint={
          <>
            Where the check runs from.{' '}
            <InfoTooltip label="About check locations">
              A location is the AWS region Soroush sends the request <em>from</em>, not anything about
              the address being checked. Running the same check from several places tells you whether a
              site is down for everyone or only unreachable from one part of the world. This deployment
              runs checks from {availableLocations.length === 1 ? 'one region' : 'these regions'} only.
            </InfoTooltip>
          </>
        }
        render={(field) => (
          <select
            {...field}
            value={selectedLocation}
            disabled={disabled || locationOptions.length === 0}
            onChange={(event) => update('locations', [event.target.value])}
          >
            {locationOptions.map((option) => (
              <option key={option} value={option}>
                {locationLabel(option)}
                {availableLocations.includes(option) ? '' : ' \u2014 not available'}
              </option>
            ))}
          </select>
        )}
      />

      {/*
        Two states the picker cannot express on its own, both stated rather than silently resolved.

        A stored location this deployment does not run from is kept and shown, because replacing it
        behind the operator's back would relocate a Check on save without anyone asking. A Check holding
        several locations predates the single-region picker; saving collapses it to the selection, so the
        form says so instead of discovering it afterwards.
      */}
      {draft.locations.some((location) => !availableLocations.includes(location)) ? (
        <p role="note" className="field-hint muted" data-testid="location-unavailable">
          This check names a location this deployment does not run from, so it is not executing. Choose
          an available location and save to fix it.
        </p>
      ) : null}
      {draft.locations.length > 1 ? (
        <p role="note" className="field-hint muted" data-testid="location-collapse">
          This check runs from {draft.locations.length} locations ({draft.locations.join(', ')}). Saving
          will set it to the one selected above.
        </p>
      ) : null}
      {locationOptions.length === 0 ? (
        <p role="alert" className="field-error" data-testid="location-none">
          No run locations are configured for this deployment, so a check cannot be scheduled. This is a
          deployment configuration problem rather than something to fix here.
        </p>
      ) : null}

      <FormField
        label="Failure threshold"
        path="check.failureThreshold"
        errors={fieldErrors}
        render={(field) => (
          <input
            {...field}
            type="number"
            value={draft.failureThreshold}
            disabled={disabled}
            onChange={(event) => update('failureThreshold', event.target.value)}
          />
        )}
      />

      <FormField
        label="Max retries"
        path="check.maxRetries"
        errors={fieldErrors}
        render={(field) => (
          <input
            {...field}
            type="number"
            value={draft.maxRetries}
            disabled={disabled}
            onChange={(event) => update('maxRetries', event.target.value)}
          />
        )}
      />

      <FormField
        label="Enabled"
        path="check.enabled"
        errors={fieldErrors}
        render={(field) => (
          <input
            {...field}
            type="checkbox"
            checked={draft.enabled}
            disabled={disabled}
            onChange={(event) => update('enabled', event.target.checked)}
          />
        )}
      />

      <FormField
        label="Latency sensitive"
        path="check.latencySensitive"
        errors={fieldErrors}
        render={(field) => (
          <input
            {...field}
            type="checkbox"
            checked={draft.latencySensitive}
            disabled={disabled}
            onChange={(event) => update('latencySensitive', event.target.checked)}
          />
        )}
      />

      <FormField
        label="Secret references"
        path="check.secretsRefs"
        errors={fieldErrors}
        hint="Comma-separated references. Never secret values."
        render={(field) => (
          <input
            {...field}
            type="text"
            value={draft.secretsRefs}
            disabled={disabled}
            onChange={(event) => update('secretsRefs', event.target.value)}
          />
        )}
      />

      {draft.type === 'uptime' ? (
        <fieldset className="form-section">
          <legend>Uptime monitor</legend>
          <FormField
            label="Mode"
            path="check.spec.mode"
            errors={fieldErrors}
            render={(field) => (
              <select
                {...field}
                value={draft.uptimeMode}
                disabled={disabled}
                onChange={(event) => update('uptimeMode', event.target.value as UptimeMode)}
              >
                {UPTIME_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            )}
          />
          <FormField
            label="Target"
            path="check.spec.target"
            errors={fieldErrors}
            hint="URL, host, or heartbeat identifier depending on the mode."
            render={(field) => (
              <input
                {...field}
                type="text"
                value={draft.target}
                disabled={disabled}
                onChange={(event) => update('target', event.target.value)}
              />
            )}
          />
          {draft.uptimeMode === 'url' ? (
            <FormField
              label="Expected status range"
              path="check.spec.expectedStatusRange"
              errors={fieldErrors}
              render={(field) => (
                <span className="input-pair">
                  <input
                    {...field}
                    type="number"
                    aria-label="Expected status minimum"
                    value={draft.statusMin}
                    disabled={disabled}
                    onChange={(event) => update('statusMin', event.target.value)}
                  />
                  <input
                    type="number"
                    aria-label="Expected status maximum"
                    value={draft.statusMax}
                    disabled={disabled}
                    onChange={(event) => update('statusMax', event.target.value)}
                  />
                </span>
              )}
            />
          ) : null}
          {draft.uptimeMode === 'tcp' ? (
            <>
              <FormField
                label="Port"
                path="check.spec.port"
                errors={fieldErrors}
                render={(field) => (
                  <input
                    {...field}
                    type="number"
                    value={draft.port}
                    disabled={disabled}
                    onChange={(event) => update('port', event.target.value)}
                  />
                )}
              />
              <FormField
                label="Connect timeout (ms)"
                path="check.spec.connectTimeoutMs"
                errors={fieldErrors}
                render={(field) => (
                  <input
                    {...field}
                    type="number"
                    value={draft.connectTimeoutMs}
                    disabled={disabled}
                    onChange={(event) => update('connectTimeoutMs', event.target.value)}
                  />
                )}
              />
            </>
          ) : null}
          {draft.uptimeMode === 'heartbeat' ? (
            <FormField
              label="Expected heartbeat interval (seconds)"
              path="check.spec.heartbeatIntervalSeconds"
              errors={fieldErrors}
              render={(field) => (
                <input
                  {...field}
                  type="number"
                  value={draft.heartbeatIntervalSeconds}
                  disabled={disabled}
                  onChange={(event) => update('heartbeatIntervalSeconds', event.target.value)}
                />
              )}
            />
          ) : null}
        </fieldset>
      ) : null}

      {draft.type === 'api' ? (
        <fieldset className="form-section">
          <legend>API check</legend>
          <FormField
            label="Response-time threshold (ms)"
            path="check.spec.responseTimeThresholdMs"
            errors={fieldErrors}
            hint="Exceeding it degrades the result."
            render={(field) => (
              <input
                {...field}
                type="number"
                value={draft.responseTimeThresholdMs}
                disabled={disabled}
                onChange={(event) => update('responseTimeThresholdMs', event.target.value)}
              />
            )}
          />
          {draft.steps.map((step, index) => (
            <fieldset key={index} className="form-subsection">
              <legend>Step {index + 1}</legend>
              <FormField
                label={`Step ${index + 1} method`}
                path={`check.spec.steps[${index}].method`}
                errors={fieldErrors}
                render={(field) => (
                  <select
                    {...field}
                    value={step.method}
                    disabled={disabled}
                    onChange={(event) => updateStep(index, { method: event.target.value as HttpMethod })}
                  >
                    {HTTP_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                )}
              />
              <FormField
                label={`Step ${index + 1} URL`}
                path={`check.spec.steps[${index}].url`}
                errors={fieldErrors}
                render={(field) => (
                  <input
                    {...field}
                    type="text"
                    value={step.url}
                    disabled={disabled}
                    onChange={(event) => updateStep(index, { url: event.target.value })}
                  />
                )}
              />
              <FormField
                label={`Step ${index + 1} expected status`}
                path={`check.spec.steps[${index}].expectedStatus`}
                errors={fieldErrors}
                render={(field) => (
                  <input
                    {...field}
                    type="number"
                    value={step.expectedStatus}
                    disabled={disabled}
                    onChange={(event) => updateStep(index, { expectedStatus: event.target.value })}
                  />
                )}
              />
              {draft.steps.length > 1 ? (
                <button
                  type="button"
                  className="secondary"
                  disabled={disabled}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      steps: current.steps.filter((_item, i) => i !== index),
                    }))
                  }
                >
                  Remove step {index + 1}
                </button>
              ) : null}
            </fieldset>
          ))}
          <button
            type="button"
            className="secondary"
            disabled={disabled}
            onClick={() =>
              setDraft((current) => ({ ...current, steps: [...current.steps, { ...EMPTY_STEP }] }))
            }
          >
            Add step
          </button>
        </fieldset>
      ) : null}

      <div className="form-actions">
        <button type="submit" disabled={disabled || !submittable || submitDisabled}>
          {/* A draft save is explicitly *not* a Check write: only the accept action creates a Check
              (Req 9.4), so the label must not imply otherwise. */}
          {props.mode === 'create' ? 'Create check' : props.mode === 'draft' ? 'Save draft' : 'Save check'}
        </button>
        {props.onCancel !== undefined ? (
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
